import { EntityApprovalStatus, entityApprovalStatus } from '@/api/entity/types';
import {
  BadgeColor,
  BadgeGroupCell,
  TableColumn,
  TextCell,
  TIME_CELL_MIN_WIDTH,
  TimeCell,
} from '@/design-system';
import { buildBlockedTooltip } from '@/utils/date';

import { EntitiesTabTableActions } from './components/entities-tab-table-actions';
import { EntitiesTabTableCountry } from './components/entities-tab-table-country';
import { EntitiesTabTableState } from './components/entities-tab-table-state';

export type EntityTabTableRow = {
  key: string;
  id: string;
  fullName: string;
  shortName: string;
  country: string | null;
  stateRegion: string | null;
  typeName: string;
  status: EntityApprovalStatus;
  fundsCount: number;
  city: string | null;
  zipCode: string | null;
  entityEmail: string | null;
  identificationNumber: string | null;
  teamMembersCount: number;
  registrationDate: string | null;
  blockedAt?: string | null;
  blockReason?: string | null;
};

export const entityStatusBadge: Record<
  EntityApprovalStatus,
  { label: string; color?: BadgeColor; tooltip?: string }
> = {
  [entityApprovalStatus.Draft]: {
    label: 'Draft',
    color: 'default',
    tooltip: 'This is a draft which has not yet been submitted for approval',
  },
  [entityApprovalStatus.Rejected]: {
    label: 'Rejected',
    color: 'destructive',
    tooltip: 'This Legal Entity was reviewed and rejected by Gamma Prime.',
  },
  [entityApprovalStatus.Verified]: {
    label: 'Verified',
    color: 'primary',
    tooltip: 'This legal entity has been approved by Gamma Prime',
  },
  [entityApprovalStatus.ReVerificationPending]: {
    label: 'Re-verification Pending',
    color: 'orange',
    tooltip:
      'This legal entity was previously verified, and it is currently waiting for a new approval from Gamma Prime after key information has been edited.',
  },
  [entityApprovalStatus.AwaitingApproval]: {
    label: 'Awaiting Approval',
    color: 'yellow',
    tooltip:
      'This legal entity has been newly created and is waiting for approval from Gamma Prime.',
  },
  [entityApprovalStatus.Blocked]: {
    label: 'Blocked',
    color: 'destructive',
    tooltip: 'This verified Entity has been blocked by Gamma Prime.',
  },
};

export const columns: TableColumn<EntityTabTableRow>[] = [
  {
    key: 'legalName',
    header: 'Full Name',
    width: '20fr',
    minWidth: 180,
    maxWidth: 320,
    sortable: true,
    cell: (row) => (
      <TextCell
        title={row.fullName}
        tooltip={row.fullName.length > 30 ? row.fullName : undefined}
      />
    ),
  },
  {
    key: 'shortName',
    header: 'Short Name',
    width: '13fr',
    sortable: true,
    cell: (row) => (
      <TextCell
        title={row.shortName}
        tooltip={row.shortName.length > 20 ? row.shortName : undefined}
      />
    ),
  },
  {
    key: 'country',
    header: 'Country',
    width: '11fr',
    sortable: true,
    cell: (row) => <EntitiesTabTableCountry country={row.country} />,
  },
  {
    key: 'state',
    header: 'State/Region',
    width: '10fr',
    cell: (row) => <EntitiesTabTableState country={row.country} stateRegion={row.stateRegion} />,
  },
  {
    key: 'type',
    header: 'Type',
    width: '14fr',
    sortable: true,
    cell: (row) => <TextCell title={row.typeName} />,
  },
  {
    key: 'status',
    header: 'Status',
    width: '14fr',
    sortable: true,
    cell: (row) => {
      const badge = entityStatusBadge[row.status];
      if (!badge) return <BadgeGroupCell badges={[]} />;
      const finalBadge =
        row.status === entityApprovalStatus.Blocked
          ? { ...badge, tooltip: buildBlockedTooltip('Entity', row.blockedAt, row.blockReason) }
          : badge;
      return <BadgeGroupCell badges={[finalBadge]} />;
    },
  },
  {
    key: 'fundsCount',
    header: '# Funds',
    width: '8fr',
    sortable: true,
    cell: (row) => <TextCell title={String(row.fundsCount)} />,
  },
  {
    key: 'city',
    header: 'City',
    width: '10fr',
    cell: (row) => <TextCell title={row.city || '—'} />,
  },
  {
    key: 'zipCode',
    header: 'ZIP / Postal Code',
    width: '8fr',
    cell: (row) => <TextCell title={row.zipCode || '—'} />,
  },
  {
    key: 'entityEmail',
    header: 'Entity Email',
    width: '15fr',
    cell: (row) => (
      <TextCell
        title={row.entityEmail || '—'}
        tooltip={row.entityEmail && row.entityEmail.length > 20 ? row.entityEmail : undefined}
      />
    ),
  },
  {
    key: 'identificationNumber',
    header: 'Identification Number',
    width: '14fr',
    cell: (row) => <TextCell title={row.identificationNumber || '—'} />,
  },
  {
    key: 'teamMembersCount',
    header: 'Team Members',
    width: '8fr',
    cell: (row) => <TextCell title={String(row.teamMembersCount)} />,
  },
  {
    key: 'registrationDate',
    header: 'Registration Date',
    width: '12fr',
    minWidth: TIME_CELL_MIN_WIDTH,
    cell: (row) =>
      row.registrationDate ? <TimeCell date={row.registrationDate} /> : <TextCell title="—" />,
  },
  {
    key: 'actions',
    header: 'Actions',
    width: '166px',
    align: 'right',
    pinned: true,
    cell: (row) => (
      <EntitiesTabTableActions id={row.id} fullName={row.fullName} status={row.status} />
    ),
  },
];

export const URL_KEYS = ['status', 'type', 'search', 'sortBy', 'sortDirection'] as const;

export const entityStatusInfo: Record<
  EntityApprovalStatus,
  { label: string; bg: string; color: string; info: string }
> = {
  [entityApprovalStatus.Draft]: {
    label: 'Draft',
    bg: '#BDBEC21A',
    color: '#BDBEC2',
    info: 'This is a draft which has not yet been submitted for approval',
  },
  [entityApprovalStatus.Rejected]: {
    label: 'Rejected',
    bg: '#E6483D1A',
    color: '#E6483D',
    info: 'This Legal Entity was reviewed and rejected by Gamma Prime.',
  },
  [entityApprovalStatus.Verified]: {
    label: 'Verified',
    bg: '#39CA8840',
    color: '#39CA88',
    info: 'This legal entity has been approved by Gamma Prime',
  },
  [entityApprovalStatus.ReVerificationPending]: {
    label: 'Re-verification Pending',
    bg: '#463A2C',
    color: '#FFAA5A',
    info: 'This legal entity was previously verified, and it is currently waiting for a new approval from Gamma Prime after key information has been edited.',
  },
  [entityApprovalStatus.AwaitingApproval]: {
    label: 'Awaiting Approval',
    bg: '#40462C',
    color: '#F9D151',
    info: 'This legal entity has been newly created and is waiting for approval from Gamma Prime.',
  },
  [entityApprovalStatus.Blocked]: {
    label: 'Blocked',
    bg: '#E6483D1A',
    color: '#E6483D',
    info: 'This verified Entity has been blocked by Gamma Prime.',
  },
};
